// src/pages/Partner/PartnerOverviewExport.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Clock3,
  Download,
  FileSpreadsheet,
  Mail,
  RefreshCw,
  Users,
} from "lucide-react";

import {
  Link,
  useOutletContext,
} from "react-router-dom";

import ExcelJS from "exceljs";

import {
  fetchPartnerAttentionDataset,
} from "../../lib/partner/partnerAttention";

import {
  fetchPartnerInvitations,
} from "../../lib/partner/partnerInvitations";


const ACTIVITY_WINDOW_DAYS = 7;
const LOGIN_INACTIVE_DAYS = 7;


const EXCEL_THEME = {
  primary: "FFFF914D",
  primaryDark: "FFE9782F",
  lightOrange: "FFFFF4EC",
  cream: "FFFFF7ED",
  border: "FFFFE1CF",
  text: "FF333333",
  muted: "FF757575",
  white: "FFFFFFFF",
};


function nullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}


function hasStartedJourney(
  learner
) {
  const levelsCompleted =
    nullableNumber(
      learner?.levels_completed
    ) || 0;

  const tasksCompleted =
    nullableNumber(
      learner?.tasks_completed
    ) || 0;

  return (
    levelsCompleted > 0 ||
    tasksCompleted > 0 ||
    Boolean(
      learner?.current_quest_id ||
        learner?.current_subquest_id ||
        learner?.current_level_key
    )
  );
}


function daysSinceTimestamp(value) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return Math.max(
    0,
    Math.floor(
      (
        Date.now() -
        date.getTime()
      ) /
        86400000
    )
  );
}


function buildInactiveRows(
  progressRows,
  cohorts
) {
  const cohortMap =
    new Map(
      (
        Array.isArray(cohorts)
          ? cohorts
          : []
      ).map(
        (cohort) => [
          String(cohort.id),
          cohort,
        ]
      )
    );

  return (
    Array.isArray(progressRows)
      ? progressRows
      : []
  )
    .filter(
      (learner) =>
        learner?.membership_status ===
        "active"
    )
    .map(
      (learner) => {
        const loginDays =
          daysSinceTimestamp(
            learner?.last_login_at
          );

        if (
          loginDays !== null
        ) {
          if (
            loginDays <
            LOGIN_INACTIVE_DAYS
          ) {
            return null;
          }

          return {
            ...learner,

            cohort_name:
              learner?.cohort_name ||
              cohortMap.get(
                String(
                  learner?.cohort_id ||
                    ""
                )
              )?.name ||
              "Cohort",

            login_inactivity_days:
              loginDays,

            login_inactivity_label:
              `${loginDays} ${
                loginDays === 1
                  ? "day"
                  : "days"
              } since login`,

            never_logged_in:
              false,
          };
        }

        const joinedDays =
          daysSinceTimestamp(
            learner?.joined_at
          );

        if (
          joinedDays === null ||
          joinedDays <
            LOGIN_INACTIVE_DAYS
        ) {
          return null;
        }

        return {
          ...learner,

          cohort_name:
            learner?.cohort_name ||
            cohortMap.get(
              String(
                learner?.cohort_id ||
                  ""
              )
            )?.name ||
            "Cohort",

          login_inactivity_days:
            joinedDays,

          login_inactivity_label:
            "Never logged in",

          never_logged_in:
            true,
        };
      }
    )
    .filter(Boolean)
    .sort(
      (a, b) =>
        (
          b.login_inactivity_days ||
          0
        ) -
        (
          a.login_inactivity_days ||
          0
        )
    );
}


function formatDate(value) {
  if (!value) {
    return "";
  }

  const date =
    new Date(
      `${value}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function formatDateTime(value) {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}


function currentPositionLabel(
  learner
) {
  if (
    learner?.last_completed_level_title
  ) {
    return learner
      .last_completed_level_title;
  }

  const parts = [
    learner?.current_quest_id
      ? `Quest ${learner.current_quest_id}`
      : null,

    learner?.current_subquest_id
      ? `Subquest ${learner.current_subquest_id}`
      : null,

    learner?.current_level_key ||
      null,
  ].filter(Boolean);

  return parts.length > 0
    ? parts.join(" · ")
    : "Not started";
}


function sanitiseFilename(value) {
  return String(
    value ||
      "partner"
  )
    .trim()
    .toLowerCase()
    .replace(
      /[^a-z0-9]+/g,
      "-"
    )
    .replace(
      /^-+|-+$/g,
      ""
    );
}


function thinBorder(
  color = EXCEL_THEME.border
) {
  return {
    top: {
      style: "thin",
      color: { argb: color },
    },
    bottom: {
      style: "thin",
      color: { argb: color },
    },
    left: {
      style: "thin",
      color: { argb: color },
    },
    right: {
      style: "thin",
      color: { argb: color },
    },
  };
}


function addWorksheetHeading({
  worksheet,
  title,
  organisationName,
  generatedAt,
  columnCount,
}) {
  const lastColumn =
    Math.max(
      columnCount,
      2
    );

  worksheet.mergeCells(
    1,
    1,
    1,
    lastColumn
  );

  const titleCell =
    worksheet.getCell(
      1,
      1
    );

  titleCell.value =
    title;

  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: {
      argb:
        EXCEL_THEME.primary,
    },
  };

  titleCell.font = {
    name: "Calibri",
    size: 18,
    bold: true,
    color: {
      argb:
        EXCEL_THEME.white,
    },
  };

  titleCell.alignment = {
    vertical: "middle",
    horizontal: "left",
  };

  worksheet.getRow(
    1
  ).height = 34;

  const metadataRows = [
    [
      "Organisation",
      organisationName ||
        "Partner organisation",
    ],
    [
      "Generated",
      formatDateTime(
        generatedAt
      ),
    ],
  ];

  metadataRows.forEach(
    ([label, value], index) => {
      const rowNumber =
        index + 2;

      const labelCell =
        worksheet.getCell(
          rowNumber,
          1
        );

      const valueCell =
        worksheet.getCell(
          rowNumber,
          2
        );

      labelCell.value =
        label;

      labelCell.font = {
        name: "Calibri",
        size: 10,
        bold: true,
        color: {
          argb:
            EXCEL_THEME.primaryDark,
        },
      };

      labelCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            EXCEL_THEME.lightOrange,
        },
      };

      if (
        lastColumn > 2
      ) {
        worksheet.mergeCells(
          rowNumber,
          2,
          rowNumber,
          lastColumn
        );
      }

      valueCell.value =
        value;

      valueCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            EXCEL_THEME.cream,
        },
      };

      valueCell.font = {
        name: "Calibri",
        size: 10,
        color: {
          argb:
            EXCEL_THEME.text,
        },
      };

      for (
        let column = 1;
        column <= lastColumn;
        column += 1
      ) {
        worksheet.getCell(
          rowNumber,
          column
        ).border =
          thinBorder();
      }
    }
  );
}


function writeCellValue(
  cell,
  header,
  rawValue
) {
  if (
    rawValue === null ||
    rawValue === undefined
  ) {
    cell.value = "";
    return;
  }

  if (
    typeof rawValue === "string" &&
    rawValue.trim().endsWith("%") &&
    /(rate|percent|percentage)/i.test(
      header
    )
  ) {
    const percentage =
      Number.parseFloat(
        rawValue
      );

    if (
      Number.isFinite(
        percentage
      )
    ) {
      cell.value =
        percentage / 100;
      cell.numFmt =
        "0.0%";
      return;
    }
  }

  cell.value =
    rawValue;

  if (
    typeof rawValue ===
    "number"
  ) {
    cell.numFmt =
      Number.isInteger(
        rawValue
      )
        ? "#,##0"
        : "#,##0.0";
  }
}


function renderTableWorksheet({
  worksheet,
  title,
  rows,
  organisationName,
  generatedAt,
}) {
  const safeRows =
    Array.isArray(rows) &&
    rows.length > 0
      ? rows
      : [
          {
            Message:
              "No data available",
          },
        ];

  const headers =
    Object.keys(
      safeRows[0]
    );

  addWorksheetHeading({
    worksheet,
    title:
      `Gwin Partner Overview · ${title}`,
    organisationName,
    generatedAt,
    columnCount:
      headers.length,
  });

  const headerRow =
    worksheet.getRow(
      5
    );

  headers.forEach(
    (header, index) => {
      const cell =
        headerRow.getCell(
          index + 1
        );

      cell.value =
        header;

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            EXCEL_THEME.primaryDark,
        },
      };

      cell.font = {
        name: "Calibri",
        size: 11,
        bold: true,
        color: {
          argb:
            EXCEL_THEME.white,
        },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      cell.border =
        thinBorder(
          EXCEL_THEME.primary
        );
    }
  );

  safeRows.forEach(
    (sourceRow, rowIndex) => {
      const row =
        worksheet.getRow(
          rowIndex + 6
        );

      headers.forEach(
        (header, columnIndex) => {
          const cell =
            row.getCell(
              columnIndex + 1
            );

          writeCellValue(
            cell,
            header,
            sourceRow?.[header]
          );

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb:
                rowIndex % 2 === 0
                  ? EXCEL_THEME.cream
                  : EXCEL_THEME.white,
            },
          };

          cell.font = {
            name: "Calibri",
            size: 11,
            color: {
              argb:
                EXCEL_THEME.text,
            },
            bold:
              columnIndex === 0,
          };

          cell.alignment = {
            vertical: "middle",
            horizontal:
              typeof cell.value ===
              "number"
                ? "right"
                : "left",
            wrapText: true,
          };

          cell.border = {
            bottom: {
              style: "hair",
              color: {
                argb:
                  EXCEL_THEME.border,
              },
            },
          };
        }
      );
    }
  );

  headers.forEach(
    (header, index) => {
      const maxLength =
        safeRows.reduce(
          (maximum, row) =>
            Math.max(
              maximum,
              String(
                row?.[header] ??
                  ""
              ).length
            ),
          header.length
        );

      worksheet.getColumn(
        index + 1
      ).width =
        Math.min(
          Math.max(
            maxLength + 3,
            13
          ),
          42
        );
    }
  );

  worksheet.autoFilter = {
    from: {
      row: 5,
      column: 1,
    },
    to: {
      row: 5,
      column:
        headers.length,
    },
  };

  worksheet.views = [
    {
      state: "frozen",
      ySplit: 5,
      activeCell: "A6",
      showGridLines: false,
    },
  ];
}


async function downloadOverviewWorkbook({
  sheets,
  filename,
  organisationName,
}) {
  const workbook =
    new ExcelJS.Workbook();

  const generatedAt =
    new Date();

  workbook.creator =
    "Gwin";
  workbook.lastModifiedBy =
    "Gwin Partner Portal";
  workbook.created =
    generatedAt;
  workbook.modified =
    generatedAt;
  workbook.company =
    "Gwin";
  workbook.subject =
    "Gwin Partner Portal overview export";
  workbook.title =
    "Gwin Partner Overview";

  sheets.forEach(
    (sheet, index) => {
      const worksheet =
        workbook.addWorksheet(
          sheet.name,
          {
            properties: {
              tabColor: {
                argb:
                  index === 0
                    ? EXCEL_THEME.primaryDark
                    : EXCEL_THEME.primary,
              },
            },
            pageSetup: {
              orientation:
                "landscape",
              fitToPage: true,
              fitToWidth: 1,
              fitToHeight: 0,
            },
          }
        );

      renderTableWorksheet({
        worksheet,
        title:
          sheet.name,
        rows:
          sheet.rows,
        organisationName,
        generatedAt,
      });
    }
  );

  const buffer =
    await workbook.xlsx
      .writeBuffer();

  const blob =
    new Blob(
      [buffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );

  const url =
    URL.createObjectURL(
      blob
    );

  const link =
    document.createElement(
      "a"
    );

  link.href =
    url;
  link.download =
    filename;

  document.body.appendChild(
    link
  );

  link.click();
  link.remove();

  URL.revokeObjectURL(
    url
  );
}


function ExportMetric({
  label,
  value,
  icon: Icon,
}) {
  return (
    <article className="rounded-[18px] border border-[#E4E4E4] bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.035)]">
      <div className="flex items-center gap-3 text-[#7A7A7A]">
        <Icon className="h-4 w-4 text-[#FF914D]" />
        <p className="text-xs font-black uppercase tracking-[0.08em]">
          {label}
        </p>
      </div>

      <p className="mt-4 text-3xl font-black tracking-[-0.04em] text-black">
        {value}
      </p>
    </article>
  );
}


function IncludedSheet({
  title,
  description,
  count,
  icon: Icon,
}) {
  return (
    <article className="flex items-start gap-4 rounded-[18px] border border-[#E6E6E6] bg-white p-5">
      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#FFF4EC] text-[#FF914D]">
        <Icon className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-black text-[#222222]">
            {title}
          </h3>

          <span className="rounded-full bg-[#F4F4F4] px-2.5 py-1 text-xs font-black text-[#666666]">
            {count}
          </span>
        </div>

        <p className="mt-1 text-sm leading-6 text-[#777777]">
          {description}
        </p>
      </div>
    </article>
  );
}


export default function PartnerOverviewExport() {
  const {
    access,
  } = useOutletContext();

  const [
    dataset,
    setDataset,
  ] = useState({
    cohorts: [],
    activeCohorts: [],
    progressRows: [],
    activeParticipants: [],
    attentionRows: [],
  });

  const [
    invitationRows,
    setInvitationRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    exporting,
    setExporting,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const canViewPending =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(
      access?.partner_role
    );

  const loadOverview =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");
          setMessage("");

          const [
            datasetResult,
            invitationsResult,
          ] =
            await Promise.allSettled(
              [
                fetchPartnerAttentionDataset(
                  access?.partner_role ||
                    ""
                ),

                canViewPending
                  ? fetchPartnerInvitations()
                  : Promise.resolve(
                      []
                    ),
              ]
            );

          if (
            datasetResult.status !==
            "fulfilled"
          ) {
            throw datasetResult.reason;
          }

          setDataset(
            datasetResult.value
          );

          if (
            invitationsResult.status ===
            "fulfilled"
          ) {
            setInvitationRows(
              Array.isArray(
                invitationsResult.value
              )
                ? invitationsResult.value
                : []
            );
          } else {
            console.error(
              "Partner overview export invitations failed:",
              invitationsResult.reason
            );

            setInvitationRows(
              []
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Partner overview export loading failed:",
            loadError
          );

          setDataset({
            cohorts: [],
            activeCohorts: [],
            progressRows: [],
            activeParticipants: [],
            attentionRows: [],
          });

          setInvitationRows(
            []
          );

          setError(
            loadError?.message ||
              "The Partner overview export could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        access?.partner_role,
        canViewPending,
      ]
    );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const pendingRows =
    useMemo(
      () =>
        invitationRows.filter(
          (invitation) =>
            invitation?.status ===
            "pending"
        ),
      [invitationRows]
    );

  const inactiveRows =
    useMemo(
      () =>
        buildInactiveRows(
          dataset.activeParticipants,
          dataset.activeCohorts
        ),
      [
        dataset.activeParticipants,
        dataset.activeCohorts,
      ]
    );

  const metrics =
    useMemo(() => {
      const activeThisWeek =
        dataset.activeParticipants.filter(
          (learner) => {
            const days =
              nullableNumber(
                learner?.days_since_activity
              );

            return (
              learner?.last_activity_at &&
              days !== null &&
              days >= 0 &&
              days <
                ACTIVITY_WINDOW_DAYS
            );
          }
        );

      return {
        totalCohorts:
          dataset.cohorts.length,

        activeCohorts:
          dataset.activeCohorts.length,

        totalParticipants:
          dataset.activeParticipants.length,

        activeThisWeek:
          activeThisWeek.length,

        activeThisWeekPercent:
          dataset.activeParticipants.length >
          0
            ? Math.round(
                (
                  activeThisWeek.length /
                  dataset.activeParticipants.length
                ) *
                  100
              )
            : 0,

        needsAttention:
          dataset.attentionRows.length,

        pending:
          canViewPending
            ? pendingRows.length
            : null,

        inactive:
          inactiveRows.length,
      };
    }, [
      canViewPending,
      dataset,
      inactiveRows.length,
      pendingRows.length,
    ]);

  const cohortPerformanceRows =
    useMemo(
      () =>
        dataset.activeCohorts.map(
          (cohort) => {
            const participants =
              dataset.activeParticipants.filter(
                (learner) =>
                  String(
                    learner?.cohort_id ||
                      ""
                  ) ===
                  String(
                    cohort.id
                  )
              );

            const attentionRows =
              dataset.attentionRows.filter(
                (learner) =>
                  String(
                    learner?.cohort_id ||
                      ""
                  ) ===
                  String(
                    cohort.id
                  )
              );

            const activated =
              participants.filter(
                hasStartedJourney
              );

            const activeLast7Days =
              participants.filter(
                (learner) => {
                  const days =
                    nullableNumber(
                      learner?.days_since_activity
                    );

                  return (
                    learner?.last_activity_at &&
                    days !== null &&
                    days >= 0 &&
                    days <
                      ACTIVITY_WINDOW_DAYS
                  );
                }
              );

            return {
              Cohort:
                cohort.name ||
                "Cohort",

              Participants:
                participants.length,

              Activated:
                activated.length,

              "Active last 7 days":
                activeLast7Days.length,

              "Active rate":
                participants.length > 0
                  ? `${Math.round(
                      (
                        activeLast7Days.length /
                        participants.length
                      ) *
                        100
                    )}%`
                  : "0%",

              "Needs attention":
                attentionRows.length,
            };
          }
        ),
      [dataset]
    );

  const workbookSheets =
    useMemo(() => {
      const summaryRows = [
        {
          Organisation:
            access?.partner_name ||
            "Partner organisation",

          "Total cohorts":
            metrics.totalCohorts,

          "Active cohorts":
            metrics.activeCohorts,

          "Participants in active cohorts":
            metrics.totalParticipants,

          "Active participants this week":
            metrics.activeThisWeek,

          "Active participants this week %":
            `${metrics.activeThisWeekPercent}%`,

          "Needs attention":
            metrics.needsAttention,

          "Pending invitations":
            canViewPending
              ? metrics.pending
              : "Not available for this role",

          "Inactive entrepreneurs":
            metrics.inactive,
        },
      ];

      const attentionRows =
        dataset.attentionRows.map(
          (learner) => ({
            "Entrepreneur name":
              learner.full_name ||
              "",

            Email:
              learner.email ||
              "",

            Cohort:
              learner.cohort_name ||
              "",

            Status:
              learner.attention_label ||
              learner.attention_status ||
              "Needs attention",

            Detail:
              learner.attention_detail ||
              "",

            Days:
              nullableNumber(
                learner.attention_days
              ) ?? "",

            "Current position":
              currentPositionLabel(
                learner
              ),

            Facilitator:
              learner.facilitator_name ||
              "",
          })
        );

      const pendingInvitationRows =
        canViewPending
          ? pendingRows.map(
              (invitation) => ({
                "Invitee name":
                  invitation.invitee_name ||
                  "",

                Email:
                  invitation.email ||
                  "",

                Cohort:
                  invitation.cohort_name ||
                  "",

                Status:
                  invitation.status ||
                  "pending",

                "Invited at":
                  formatDateTime(
                    invitation.invited_at
                  ),

                "Expires at":
                  formatDateTime(
                    invitation.expires_at
                  ),
              })
            )
          : [];

      const inactiveEntrepreneurRows =
        inactiveRows.map(
          (learner) => ({
            "Entrepreneur name":
              learner.full_name ||
              "",

            Email:
              learner.email ||
              "",

            Cohort:
              learner.cohort_name ||
              "",

            Inactivity:
              learner.login_inactivity_label ||
              "",

            "Inactive days":
              learner.login_inactivity_days ||
              0,

            "Last successful login":
              formatDateTime(
                learner.last_login_at
              ),

            "Joined cohort":
              formatDateTime(
                learner.joined_at
              ),

            "Current position":
              currentPositionLabel(
                learner
              ),
          })
        );

      const allCohortsRows =
        dataset.cohorts.map(
          (cohort) => ({
            Cohort:
              cohort.name ||
              "Cohort",

            Status:
              cohort.status ||
              "",

            "Programme type":
              cohort.programme_type ||
              "",

            "Start date":
              formatDate(
                cohort.start_date
              ),

            "End date":
              formatDate(
                cohort.end_date
              ),

            Capacity:
              cohort.capacity ===
                null ||
              cohort.capacity ===
                undefined
                ? "Unlimited"
                : cohort.capacity,

            "Registered learners":
              nullableNumber(
                cohort.learner_count
              ) ?? 0,

            "Active learners":
              nullableNumber(
                cohort.active_learner_count
              ) ?? 0,

            "Pending invitations":
              nullableNumber(
                cohort.pending_invitation_count
              ) ?? 0,
          })
        );

      const sheets = [
        {
          name:
            "Overview Summary",
          rows:
            summaryRows,
        },
        {
          name:
            "Cohort Performance",
          rows:
            cohortPerformanceRows,
        },
        {
          name:
            "Needs Attention",
          rows:
            attentionRows,
        },
      ];

      if (
        canViewPending
      ) {
        sheets.push({
          name:
            "Pending Invitations",
          rows:
            pendingInvitationRows,
        });
      }

      sheets.push(
        {
          name:
            "Inactive Entrepreneurs",
          rows:
            inactiveEntrepreneurRows,
        },
        {
          name:
            "All Cohorts",
          rows:
            allCohortsRows,
        }
      );

      return sheets;
    }, [
      access?.partner_name,
      canViewPending,
      cohortPerformanceRows,
      dataset,
      inactiveRows,
      metrics,
      pendingRows,
    ]);

  const handleExport =
    useCallback(
      async () => {
        try {
          setExporting(true);
          setError("");
          setMessage("");

          if (
            dataset.cohorts.length ===
            0
          ) {
            throw new Error(
              "There is no Partner overview data to export yet."
            );
          }

          const today =
            new Date()
              .toISOString()
              .slice(0, 10);

          const organisationSlug =
            sanitiseFilename(
              access?.partner_name ||
                "partner"
            );

          await downloadOverviewWorkbook({
            sheets:
              workbookSheets,

            filename:
              `${organisationSlug || "partner"}-overview-${today}.xlsx`,

            organisationName:
              access?.partner_name ||
              "Partner organisation",
          });

          setMessage(
            "Overview Excel workbook exported successfully."
          );
        } catch (
          exportError
        ) {
          console.error(
            "Partner overview export failed:",
            exportError
          );

          setError(
            exportError?.message ||
              "The overview workbook could not be exported."
          );
        } finally {
          setExporting(false);
        }
      },
      [
        access?.partner_name,
        dataset.cohorts.length,
        workbookSheets,
      ]
    );

  if (
    loading
  ) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-[28px] border border-[#EFEFEF] bg-white">
        <div className="text-center">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

          <p className="mt-3 text-sm font-bold text-[#757575]">
            Loading overview export…
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Link
        to="/partner"
        className="inline-flex items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to overview
      </Link>

      <header className="mt-5 rounded-[28px] border border-[#FFE1CF] bg-white p-6 shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Overview export
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Export organisation overview
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-[#757575] md:text-base">
              Download the high-level Partner dashboard data across your cohorts in one Excel workbook. Detailed learner reporting remains available from each individual cohort.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={
                loadOverview
              }
              disabled={
                exporting
              }
              className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] px-5 py-3 text-sm font-black transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </button>

            <button
              type="button"
              onClick={
                handleExport
              }
              disabled={
                exporting ||
                dataset.cohorts.length ===
                  0
              }
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}

              {exporting
                ? "Exporting…"
                : "Export Excel"}
            </button>
          </div>
        </div>
      </header>

      {message && (
        <div className="mt-5 rounded-[18px] border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800">
          {message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-5 rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}

      <section className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ExportMetric
          label="Active cohorts"
          value={
            metrics.activeCohorts
          }
          icon={Users}
        />

        <ExportMetric
          label="Active participants"
          value={
            metrics.totalParticipants
          }
          icon={Users}
        />

        <ExportMetric
          label="Needs attention"
          value={
            metrics.needsAttention
          }
          icon={AlertTriangle}
        />

        <ExportMetric
          label="Inactive"
          value={
            metrics.inactive
          }
          icon={Clock3}
        />
      </section>

      <section className="mt-6 overflow-hidden rounded-[22px] border border-[#E1E1E1] bg-white shadow-sm">
        <div className="border-b border-[#E8E8E8] px-5 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#FF914D]">
              <FileSpreadsheet className="h-5 w-5" />
            </div>

            <div>
              <h2 className="text-xl font-black tracking-[-0.02em] text-[#222222]">
                Workbook contents
              </h2>

              <p className="mt-1 text-sm text-[#777777]">
                The export mirrors the high-level information already available from the Partner overview.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 p-5 md:grid-cols-2 sm:p-6">
          <IncludedSheet
            title="Overview summary"
            description="Organisation-wide counts and engagement metrics from the overview dashboard."
            count={1}
            icon={BarChart3}
          />

          <IncludedSheet
            title="Cohort performance"
            description="Participants, activation, seven-day activity and attention totals for active cohorts."
            count={
              cohortPerformanceRows.length
            }
            icon={BarChart3}
          />

          <IncludedSheet
            title="Needs attention"
            description="Entrepreneurs currently classified as stalled, inactive or on watch."
            count={
              dataset.attentionRows.length
            }
            icon={AlertTriangle}
          />

          {canViewPending && (
            <IncludedSheet
              title="Pending invitations"
              description="Pending cohort invitations that have not yet been accepted."
              count={
                pendingRows.length
              }
              icon={Mail}
            />
          )}

          <IncludedSheet
            title="Inactive entrepreneurs"
            description="Active cohort members with no successful login for seven days or more."
            count={
              inactiveRows.length
            }
            icon={Clock3}
          />

          <IncludedSheet
            title="All cohorts"
            description="High-level cohort setup, learner totals, dates, capacity and current stored status."
            count={
              dataset.cohorts.length
            }
            icon={Users}
          />
        </div>
      </section>
    </>
  );
}
